<template>
  <VMenu
    v-model="isOpen"
    location="bottom end"
    :closeOnContentClick="false"
    offset="8"
  >
    <template #activator="{ props: menuProps }">
      <button
        type="button"
        v-bind="menuProps"
        class="the-header-profile-menu__activator d-inline-flex align-center ga-3 border-0 bg-transparent cursor-pointer pa-0"
        aria-haspopup="menu"
        :aria-expanded="isOpen"
      >
        <VAvatar size="40" color="#F4F0EB" rounded="circle">
          <VIcon icon="user" size="24" color="gray-600" />
        </VAvatar>
        <span
          v-if="!compact"
          class="text-gray-600 font-weight-medium text-no-wrap"
        >
          {{ headerDisplayName }}
        </span>
        <VIcon v-if="!compact" icon="chevronDown" size="18" color="gray-500" />
      </button>
    </template>

    <VList
      class="the-header-profile-menu__list py-2 rounded-lg"
      minWidth="240"
      elevation="2"
    >
      <VListItem
        class="the-header-profile-menu__name px-4 py-2"
        :title="headerDisplayName"
        :subtitle="profileSubtitle"
      />

      <VDivider class="my-1 border-opacity-25" />

      <VListItem
        title="خروج"
        prependIcon="logout"
        class="text-error cursor-pointer"
        @click="onLogoutClick"
      />
    </VList>
  </VMenu>

  <BaseConfirmationModal
    v-model="isLogoutOpen"
    color="error"
    title="خروج از حساب کاربری"
    description="آیا مطمئن هستید که می‌خواهید از حساب کاربری خود خارج شوید؟"
    :state="ConfirmationState.Logout"
    @update:is-open="toggleLogoutModal"
    @on-submit="logout"
  />
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
  VAvatar,
  VDivider,
  VIcon,
  VList,
  VListItem,
  VMenu,
} from 'vuetify/components'
import type { Props } from './TheHeaderProfileMenu.types'
import BaseConfirmationModal from '@/modules/Core/components/base/BaseConfirmationModal/BaseConfirmationModal.vue'
import { useAuthStore } from '@/modules/Auth/store'
import { logoutRequest } from '@/modules/Auth/requests/mutations/useLogoutMutation'
import { ConfirmationState } from '@/modules/Core/types/model/confirmationModal'
import { useCurrentEmployeeProfile } from '@/modules/User/composables/useCurrentEmployeeProfile'

defineOptions({
  name: 'TheHeaderProfileMenu',
})

withDefaults(defineProps<Props>(), {
  compact: false,
})

const router = useRouter()
const authStore = useAuthStore()
const { fullName, status, positionTitle } = useCurrentEmployeeProfile()

const isOpen = ref(false)
const isLogoutOpen = ref(false)

const headerDisplayName = computed(() => {
  if (status.value === 'loading') {
    return '…'
  }

  return fullName.value || '—'
})

const profileSubtitle = computed(() => {
  const title = positionTitle.value
  return title && title !== '—' ? title : undefined
})

function onLogoutClick() {
  isOpen.value = false
  toggleLogoutModal()
}

function toggleLogoutModal() {
  isLogoutOpen.value = !isLogoutOpen.value
}

async function logout() {
  const refreshToken = authStore.refreshToken

  try {
    if (refreshToken) {
      await logoutRequest({ refreshToken })
    }
  } catch {
    // Best-effort server logout; local session is always cleared.
  } finally {
    authStore.logout()
    toggleLogoutModal()
    await router.push('/login')
  }
}
</script>

<style scoped>
.the-header-profile-menu__activator:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
  border-radius: 999px;
}

.the-header-profile-menu__name :deep(.v-list-item-title) {
  font-weight: 600;
  color: rgb(var(--v-theme-gray-800));
}
</style>
